import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Button, Card } from '@/ui/primitives.js';
import { PageHeader } from '@/ui/Layout.js';
import { useAuth } from '@/stores/auth.js';
import { cameraDistance } from '@/lib/units.js';
import { GUIDE_ROWS, guideSummary } from '@/features/onboarding/guideContent.js';
import { SetupIllustration } from '@/features/onboarding/SetupIllustration.js';
import { CaptureSetupPage } from './CaptureSetupPage.js';

const SEEN_KEY = 'erectus.phoneGuideSeen';

function seen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * The capture route. The first time someone opens a session on this phone
 * (and has none recorded yet), how to place the phone comes first — here,
 * where it is needed, instead of as an onboarding step. The camera only
 * starts after "Open the camera" (CLAUDE.md capture invariant 1).
 */
export function CaptureEntry(): ReactNode {
  const { userDoc } = useAuth();
  const hasSessions = (userDoc?.gamification?.lastSessionAt ?? 0) > 0;
  const [show, setShow] = useState(() => !hasSessions && !seen());
  if (!show) return <CaptureSetupPage />;

  const done = (): void => {
    try {
      localStorage.setItem(SEEN_KEY, '1');
    } catch {
      // Private mode: the guide shows again next time, which is harmless.
    }
    setShow(false);
  };

  // Camera routes have no app frame (ui/Layout "bare"), so this lays out its own.
  return (
    <div
      className="mx-auto h-dvh w-full max-w-screen-sm space-y-4 overflow-y-auto bg-surface px-4"
      style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1rem)', paddingBottom: 'calc(env(safe-area-inset-bottom) + 1.5rem)' }}
    >
      <PageHeader
        title="Place your phone"
        subtitle="Once, before your first check."
        action={
          <Link to="/progress" className="text-sm font-semibold underline">
            Close
          </Link>
        }
      />
      <Card>
        <SetupIllustration />
      </Card>
      <Card className="space-y-3">
        <ul className="space-y-2">
          {GUIDE_ROWS.slice(0, 5).map((row) => (
            <li key={row.id} className="flex gap-2 text-sm">
              <span aria-hidden>•</span>
              <span>
                <strong className="font-medium">{row.title}. </strong>
                {guideSummary(row, cameraDistance())}
              </span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted">
          The next screen checks all of this live before the session starts.{' '}
          <Link to="/guide" className="underline">
            Full guide
          </Link>
        </p>
      </Card>
      <Button className="w-full" onClick={done}>
        Open the camera
      </Button>
    </div>
  );
}
